export const CHECKOUT_SOCIAL_AUTH_FLOW_KEY = "checkoutV2SocialAuthFlow";
export const CHECKOUT_SOCIAL_AUTH_FLOW_TTL = 30 * 60 * 1000;

const supportedProviders = ["apple", "google"];

export const sanitizeSocialAuthReturnTo = (
  value,
  fallback = "/checkout"
) => {
  const path = `${value || ""}`.trim().slice(0, 500);
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    Array.from(path).some((character) => character.charCodeAt(0) < 32)
  )
    return fallback;
  return path;
};

const getSessionStorage = (storage) => {
  if (storage) return storage;
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
};

export const beginCheckoutSocialAuth = (
  provider,
  returnTo,
  storage,
  now = Date.now(),
  scrollY
) => {
  if (!supportedProviders.includes(provider)) return null;
  const currentScrollY = Number(
    scrollY ??
      (typeof window === "undefined"
        ? 0
        : window.pageYOffset || window.scrollY || 0)
  );
  const flow = {
    provider,
    returnTo: sanitizeSocialAuthReturnTo(returnTo),
    startedAt: Number(now),
    scrollY:
      Number.isFinite(currentScrollY) && currentScrollY > 0
        ? Math.round(currentScrollY)
        : 0,
  };
  try {
    const target = getSessionStorage(storage);
    if (target)
      target.setItem(CHECKOUT_SOCIAL_AUTH_FLOW_KEY, JSON.stringify(flow));
  } catch (error) {}
  return flow;
};

export const readCheckoutSocialAuth = (
  storage,
  now = Date.now()
) => {
  try {
    const value = getSessionStorage(storage)?.getItem(
      CHECKOUT_SOCIAL_AUTH_FLOW_KEY
    );
    if (!value) return null;
    const flow = JSON.parse(value);
    const startedAt = Number(flow?.startedAt);
    if (
      !supportedProviders.includes(flow?.provider) ||
      !Number.isFinite(startedAt) ||
      startedAt <= 0 ||
      Number(now) - startedAt > CHECKOUT_SOCIAL_AUTH_FLOW_TTL
    ) {
      clearCheckoutSocialAuth(storage);
      return null;
    }
    return {
      provider: flow.provider,
      returnTo: sanitizeSocialAuthReturnTo(flow.returnTo),
      startedAt,
      scrollY:
        Number.isFinite(Number(flow.scrollY)) && Number(flow.scrollY) > 0
          ? Math.round(Number(flow.scrollY))
          : 0,
    };
  } catch (error) {
    clearCheckoutSocialAuth(storage);
    return null;
  }
};

export const restoreCheckoutSocialAuthScroll = (
  flow,
  targetWindow = typeof window === "undefined" ? null : window
) => {
  const scrollY = Number(flow?.scrollY);
  if (!targetWindow || !Number.isFinite(scrollY) || scrollY <= 0) return false;
  const restore = () => targetWindow.scrollTo(0, scrollY);
  if (typeof targetWindow.requestAnimationFrame === "function")
    targetWindow.requestAnimationFrame(restore);
  else restore();
  return true;
};

export const clearCheckoutSocialAuth = (storage) => {
  try {
    const target = getSessionStorage(storage);
    if (target) target.removeItem(CHECKOUT_SOCIAL_AUTH_FLOW_KEY);
  } catch (error) {}
};

export const parseSocialAuthCallback = (search = "") => {
  const params = new URLSearchParams(search || "");
  const success = params.get("success");
  const token = params.get("auth_key") || "";
  const present = success !== null || !!token;
  return {
    present,
    successful: present && success == 1 && !!token,
    token,
  };
};

export const buildSocialAuthRedirect = (
  provider,
  domain,
  production = process.env.NODE_ENV === "production"
) => {
  if (!supportedProviders.includes(provider)) return null;
  const endpoint = `initiate_${provider}_auth`;
  if (!production)
    return `http://localhost:8000/${endpoint}/?domain=localhost`;
  return `https://www.yes-login.com/${endpoint}/?domain=${encodeURIComponent(
    `${domain || ""}`.trim()
  )}`;
};
